import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Email from "../../../../../Assets/images/mail-icon.svg";
import BackIcon from "../../../../../Assets/images/back-icon.svg";
import VerifyIcon from "../../../../../Assets/images/complete-icon.svg";

import { OTP_EXPIRY_TIME_SECONDS } from '../../Utils/Constants';
import Loader from '../../../../../Context/Loader/Loader';
import { showErrorToast } from '../../../../../Context/Toaster/Toaster';

const basePath = process.env.REACT_APP_SERVER_BASE_URL;
const signUpPath = process.env.REACT_APP_CC_SIGNUP_BASE_PATH;

const OtpVerify = ({ email, onOtpSuccess, onBack, onResend }) => {
  const [otp, setOtp] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(OTP_EXPIRY_TIME_SECONDS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);
  const errorRef = useRef(null);

  useEffect(() => {
    inputRefs.current[0]?.focus();
    const timer = setInterval(() => setTimeLeft(prev => Math.max(prev - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleChange = (e, i) => {
    const input = e.target.value.replace(/\D/g, '');

    if (input.length === 4) {
      const newOtp = input.split('').slice(0, 4);
      setOtp(newOtp);
      inputRefs.current[3]?.focus();
      if (error) setError('');
      return;
    }

    const val = input.slice(0, 1);
    const updated = [...otp];
    updated[i] = val;
    setOtp(updated);
    if (error) setError('');
    if (val && i < 3) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (e, i) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (validateOtp()) handleVerify();
    }
  };

  const validateOtp = () => {
    const joined = otp.join('');
    if (joined.length < 4) {
      setError('Please enter the complete 4-digit code');
      inputRefs.current[otp.findIndex(v => v === '')]?.focus();
      errorRef.current?.scrollIntoView({ behavior: 'smooth' });
      return false;
    }
    if (!/^\d{4}$/.test(joined)) {
      setError('The code should contain only numbers');
      errorRef.current?.scrollIntoView({ behavior: 'smooth' });
      return false;
    }
    return true;
  };

  const handleVerify = async () => {
    if (isSubmitting || !validateOtp()) return;
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${basePath}${signUpPath}/verify-otp`, {
        email,
        otp: otp.join('')
      });

      if (response.data.status === 'SUCCESS') {
        onOtpSuccess?.(otp.join(''));
      } else {
        showErrorToast(response.data.message);
        errorRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (err) {
      const msg = err.response? err.response.data.message : err.message;
      console.error(msg);
      showErrorToast(msg);
      errorRef.current?.scrollIntoView({ behavior: 'smooth' });
      console.error('OTP error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = () => {
    setOtp(['', '', '', '']);
    setTimeLeft(OTP_EXPIRY_TIME_SECONDS);
    setError('');
    inputRefs.current[0]?.focus();
    onResend?.();
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div
      className=""
      style={{
        backgroundColor: "white",
        padding: "40px",
        borderRadius: "20px",
        position: "relative",
        width: "80%",
        margin: "0 auto"
      }}
    >
      <div className="" style={{ width: '100%', float: 'none', padding: '0', margin: '0 auto' }}>
        <div className="mail-image">
          <img src={Email} alt="Email" />
        </div>

        <div className="otp-header" style={{ textAlign: 'center' }}>
          <h1 style={{ fontWeight: '600', fontSize: '1.5rem', marginBottom: '0.5rem' }}>Check your email</h1>
          <h2 style={{ fontWeight: 'normal', fontSize: '1rem', marginBottom: '0.25rem' }}>
            Enter the verification code sent to
          </h2>
          <p style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '1rem' }}>{email}</p>
        </div>

        <div className="hform-inner-small" style={{ width: '100%' }}>
          <div className="hotp-split mb-0">
            <div className="otp-input-container" style={{ width: '100%', marginBottom: '1.5rem', position: 'relative' }}>
              <div className="otp-input" style={{ display: 'flex', margin: '8px 0', justifyContent: 'center' }}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={digit}
                    onChange={(e) => handleChange(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    ref={(el) => (inputRefs.current[i] = el)}
                    disabled={isSubmitting}
                    style={{
                      width: '50px',
                      height: '50px',
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      border: `1px solid ${digit === '' && error ? '#ff0000' : '#ccc'}`,
                      borderRadius: '4px',
                      backgroundColor: digit === '' && error ? '#fff0f0' : 'white',
                    }}
                    aria-invalid={!!error}
                    aria-describedby={error ? "otp-error" : undefined}
                  />
                ))}
              </div>
              {error && (
                <div
                  id="otp-error"
                  ref={errorRef}
                  role="alert"
                  style={{
                    color: '#ff0000',
                    textAlign: 'center',
                    fontSize: '14px',
                    marginTop: '8px',
                    position: 'absolute',
                    width: '100%',
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            <div
              className="timer-cvr"
              style={{
                textAlign: 'center',
                marginBottom: '1rem',
                minHeight: '50px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {timeLeft > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '15px',
                    fontWeight: '400',
                    height: '30px',
                  }}
                >
                  <button
                    className="r-otp"
                    disabled
                    style={{
                      background: 'none',
                      border: 'none',
                      fontWeight: '400',
                      fontSize: '15px',
                      padding: 0,
                      color: '#000',
                    }}
                  >
                    <span>Resend</span> Code in
                  </button>
                  <div
                    id="timer"
                    style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {formatTime(timeLeft)}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '15px',
                    fontWeight: '400',
                    height: '30px',
                  }}
                >
                  <span>Didn't get the code?</span>
                  <button
                    onClick={handleResend}
                    disabled={isSubmitting}
                    style={{
                      fontWeight: '500',
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      color: '#000',
                      padding: 0,
                      fontSize: '15px',
                      cursor: 'pointer',
                    }}
                  >
                    Resend Code
                  </button>
                </div>
              )}
            </div>

            <div className="ob-cvr" style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="h-btn ms-0 mt-0"
                onClick={handleVerify}
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                style={{
                  backgroundColor: '#59A52C',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  display: 'inline-flex',
                  marginTop: '20px',
                  marginLeft: '2%',
                  width: '128px',
                  height: '46px',
                }}
              >
                {isSubmitting ? <Loader/> : 'Verify'} <img src={VerifyIcon} alt="" />
              </button>
            </div>
          </div>
        </div>

        <div className="field btns bts-cvr" style={{ marginTop: '0', marginLeft: '0' }}>
          <button className="prev-1 prev back-btn" onClick={onBack} disabled={isSubmitting} style={{ background: 'none', border: 'none' }}>
            <img src={BackIcon} alt="" />
          </button>
        </div>
      </div>
    </div >
  );
};

export default OtpVerify;
